/**
 * ScenePropsSystem — individually placed objects that sort against the walker.
 *
 * Full-width layers give a scene its planes; props give it *occupancy*. These
 * are separate sprites with their own normalised position, so they take the
 * scene's perspective scale (further back = smaller) and a depth derived from
 * where they stand — which means the walker passes behind the ones above them
 * and in front of the ones below, without anything being authored per-pair.
 *
 * Depth uses the same bracket arithmetic the layer manager does, so props,
 * layers and the walker all live on one scale:
 *
 *     depth = bracket * BRACKET_DEPTH_SPACING + within-bracket rank
 *
 * The walker takes the top rank of its bracket (…99), so a prop sharing a
 * bracket sits behind it and a prop a bracket nearer sits in front. Crossing a
 * threshold is what reads as passing something.
 *
 * See docs/design/single-scene-game-spec.md — these are also Phase 3's fuel
 * sources, which is why the set is deadfall, kindling and a stump rather than
 * decoration.
 */
import * as Phaser from 'phaser';
import type { PaintedSceneAssetSpec } from './sceneAssetSpec.schema';
import { samplePerspectiveScale, yToDepthBracket } from './sceneAssetSpec.schema';
import { bestLightAt, canGather, wearScale, type RadialLight } from './hearthRules';

/** Must match ParallaxLayerManager's spacing or the two scales diverge. */
const BRACKET_DEPTH_SPACING = 100;

export interface ScenePropSpec {
  /** Texture key; also the prop's id. */
  key: string;
  /** Normalised position of the prop's base (its feet on the ground). */
  nx: number;
  ny: number;
  /** Multiplies the perspective scale at that y. */
  scale?: number;
  /** Mirror horizontally, so the same art can be reused without reading as a copy. */
  flip?: boolean;
  /** Fraction of the scene width the prop should span at scale 1. */
  widthFrac?: number;
  /**
   * Fuel yielded by one armful taken from this prop, 0..1 of the fire's
   * capacity. Absent means the prop is scenery — a fern is not firewood.
   */
  fuel?: number;
  /**
   * How many armfuls are in it before it is picked clean. The prop visibly
   * diminishes with each one and goes quiet when spent, so the clearing is
   * stripped over the course of a night and the player has to range further —
   * a change in world state rather than in flavour, which is what the evidence
   * says makes a choice feel like one.
   */
  armfuls?: number;
}

/** A light falling on the scene, in WORLD space. */
export type PropLight = RadialLight;

/** A fuel prop the walker can currently take from. */
export interface GatherTarget {
  /** Index into the system's own prop list — the handle for `take`. */
  id: number;
  /** Normalised position of the prop's base. */
  nx: number;
  ny: number;
  /** Fuel one armful yields. */
  fuel: number;
  /** Texture key, so the walker can be shown carrying it. */
  key: string;
}

interface PropRuntime {
  spec: ScenePropSpec;
  img: Phaser.GameObjects.Image;
  /** Armfuls left. 0 for scenery and for anything picked clean. */
  remaining: number;
  /** How brightly the fire is currently reaching it, 0..1. */
  litness: number;
  /** Shrink factor applied as the prop is worked down. 1 until first taken. */
  scaleMul: number;
  /** Running while she is working this pile, so it can be stopped on interrupt. */
  workTween: Phaser.Tweens.Tween | null;
}

export class ScenePropsSystem {
  private readonly scene: Phaser.Scene;
  private readonly spec: PaintedSceneAssetSpec;
  private readonly props: PropRuntime[] = [];
  private lit = false;

  constructor(scene: Phaser.Scene, spec: PaintedSceneAssetSpec, list: ScenePropSpec[]) {
    this.scene = scene;
    this.spec = spec;
    for (const p of list) {
      if (!scene.textures.exists(p.key)) continue;
      const img = scene.add.image(0, 0, p.key);
      // Origin at the base so a prop grows upward from where it stands, the
      // same way the walker does.
      img.setOrigin(0.5, 1);
      if (p.flip) img.setFlipX(true);
      this.props.push({
        spec: p,
        img,
        remaining: p.fuel ? (p.armfuls ?? 1) : 0,
        litness: 0,
        scaleMul: 1,
        workTween: null,
      });
    }
    this.layout();
  }

  // ── gathering ─────────────────────────────────────────────────────────────

  /**
   * The nearest fuel prop to a normalised point that is both unspent and inside
   * the firelight, or null.
   *
   * The light gate is the point of Phase 2: what the fire reaches is what you
   * can reach. It also produces the escalation the scene needs for free — the
   * near deadfall runs out, and the sources further out only come into reach
   * once the fire is high, so you have to build it up before you can afford
   * the trip that keeps it up.
   *
   * `bringingLight` lifts the gate, and only the lantern sets it: walking a
   * lamp over to a woodpile is precisely the case where "you cannot see it
   * from here" is the wrong answer.
   */
  findGatherable(worldX: number, worldY: number, slopPx: number, bringingLight = false): GatherTarget | null {
    // Sprite bounds first, so clicking the *body* of a fallen log works —
    // matching on distance to its base instead would make the near half of
    // every prop dead to the pointer.
    let best: PropRuntime | null = null;
    let bestId = -1;
    let bestD = Infinity;

    for (let i = 0; i < this.props.length; i++) {
      const p = this.props[i];
      if (p.remaining <= 0 || !p.spec.fuel) continue;
      // Normally you can only reach for what the light already touches. Not
      // when you are carrying the light *to* it: the far deadfall could never
      // be clicked, because it is not lit until the lantern arrives, and the
      // lantern only arrives if you can say where you are taking it. The gate
      // was circular and the far half of the grove stayed shut.
      if (!bringingLight && !canGather(p.litness)) continue;

      const b = p.img.getBounds();
      const inside = b.contains(worldX, worldY);
      // Distance to the sprite's rectangle, 0 when inside it. Ties between
      // overlapping props go to the nearer of the two.
      const dx = Math.max(b.left - worldX, 0, worldX - b.right);
      const dy = Math.max(b.top - worldY, 0, worldY - b.bottom);
      const d = inside ? 0 : Math.hypot(dx, dy);
      if (d > slopPx || d >= bestD) continue;
      // Among exact hits, the one drawn in front wins — that is the one the
      // player believes they clicked.
      if (d === 0 && bestD === 0 && best && p.img.depth < best.img.depth) continue;
      bestD = d;
      best = p;
      bestId = i;
    }

    if (!best || !best.spec.fuel) return null;
    return { id: bestId, nx: best.spec.nx, ny: best.spec.ny, fuel: best.spec.fuel, key: best.spec.key };
  }

  /**
   * She is working this pile. It rocks under her hands until she stops.
   *
   * This is the entire progress readout for a gather — no bar, no timer, no
   * number. The scene is the interface (see the spec's "The scene is the
   * interface"), so "something is happening here and it is not finished" has to
   * be said by the thing itself.
   */
  startWorking(id: number): void {
    const p = this.props[id];
    if (!p || p.workTween) return;
    p.workTween = this.scene.tweens.add({
      targets: p.img,
      rotation: p.img.rotation + 0.05,
      duration: 190,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /** Stop working, whether she finished or was called away. */
  stopWorking(id: number): void {
    const p = this.props[id];
    if (!p?.workTween) return;
    p.workTween.stop();
    p.workTween = null;
    p.img.setRotation(0);
  }

  /** Is there anything left in this pile at all? */
  canTake(id: number): boolean {
    const p = this.props[id];
    return Boolean(p && p.remaining > 0 && p.spec.fuel);
  }

  /**
   * Can she work this pile *right now* — anything left, and enough light on it?
   *
   * The light half matters while she is mid-gather: the fire has been burning
   * the whole time she walked out and the whole time she has been working, and
   * if it has fallen far enough that this pile is back in the dark she cannot
   * go on breaking it up. That is what makes letting the fire drop while you
   * are away from it cost something.
   */
  canGatherNow(id: number): boolean {
    const p = this.props[id];
    return Boolean(p && p.remaining > 0 && p.spec.fuel && canGather(p.litness));
  }

  /**
   * Take one armful. Returns the fuel gained, or 0 if there was nothing left.
   * The prop shrinks as it is worked down and fades out when it is spent, so
   * the state of the clearing is legible without a single number on screen.
   */
  take(id: number): number {
    const p = this.props[id];
    if (!p || p.remaining <= 0 || !p.spec.fuel) return 0;
    this.stopWorking(id);
    p.remaining -= 1;
    const total = p.spec.armfuls ?? 1;

    // Held on the runtime rather than poked into the sprite, so a resize
    // re-derives it instead of resetting it.
    p.scaleMul = wearScale(p.remaining, total);
    const target = this.baseScaleOf(p) * p.scaleMul;
    this.scene.tweens.add({
      targets: p.img,
      scaleX: target,
      scaleY: target,
      alpha: p.remaining <= 0 ? 0 : 1,
      duration: p.remaining <= 0 ? 520 : 260,
      ease: p.remaining <= 0 ? 'Sine.easeIn' : 'Back.easeOut',
      onComplete: () => {
        if (p.remaining <= 0) p.img.setVisible(false);
      },
    });
    return p.spec.fuel;
  }

  /** Normalised base position of a prop, for approach pathing. */
  positionOf(id: number): { nx: number; ny: number } | null {
    const p = this.props[id];
    return p ? { nx: p.spec.nx, ny: p.spec.ny } : null;
  }

  /** World position of a prop's base, for lighting. */
  worldPositionOf(id: number): { x: number; y: number } | null {
    const p = this.props[id];
    return p ? { x: p.img.x, y: p.img.y } : null;
  }

  /** First prop with this texture key, or -1. */
  idOf(key: string): number {
    return this.props.findIndex(p => p.spec.key === key);
  }

  /**
   * Stand a prop somewhere else. The lantern is the reason this exists: it is
   * scenery you can pick up and set back down, so its place in the clearing is
   * the player's to decide rather than the author's.
   *
   * Depth and perspective scale are re-derived from the new y, so a lantern
   * carried up the frame gets smaller and sorts behind things it should.
   */
  moveProp(id: number, nx: number, ny: number): void {
    const p = this.props[id];
    if (!p) return;
    p.spec = { ...p.spec, nx, ny };
    this.layout();
  }

  setPropVisible(id: number, visible: boolean): void {
    this.props[id]?.img.setVisible(visible);
  }

  /** Rendered height of a prop, for anchoring things to its top. */
  displayHeightOf(id: number): number {
    return this.props[id]?.img.displayHeight ?? 0;
  }

  /** Screen-bounds of a prop, for hit-testing a click against it. */
  boundsOf(id: number): Phaser.Geom.Rectangle | null {
    const p = this.props[id];
    return p && p.img.visible ? p.img.getBounds() : null;
  }

  /** Re-place every prop. Call on create and on resize. */
  layout(): void {
    // Props stand in the world, so normalised x spans the world not the screen.
    const w = this.scene.scale.width * (this.spec.worldWidthFactor ?? 1);
    const h = this.scene.scale.height;
    for (const p of this.props) {
      p.img.setScale(this.baseScaleOf(p) * p.scaleMul);
      p.img.setPosition(p.spec.nx * w, p.spec.ny * h);
      p.img.setDepth(this.depthFor(p.spec.ny));
    }
  }

  /** Scale a prop would have untouched, before any gathering has worn it down. */
  private baseScaleOf(p: PropRuntime): number {
    const perspective = samplePerspectiveScale(p.spec.ny, this.spec.perspective);
    const widthFrac = p.spec.widthFrac ?? 0.16;
    // Normalise to a fraction of the scene, so a 900px source and a 490px
    // source end up the same size on screen for the same widthFrac.
    const base = (widthFrac * this.scene.scale.width) / p.img.width;
    return base * perspective * (p.spec.scale ?? 1);
  }

  /**
   * Light the props from the fire.
   *
   * A dark plate with a hole in it makes the clearing look lit, but the objects
   * inside it are still painted with whatever light they were generated under —
   * so a log a metre from the flames and a stump at the treeline are the same
   * colour, and the eye reads the pool as a filter laid over the scene rather
   * than as light falling on things. Tinting each prop by its distance from the
   * fire is what turns "a bright patch" into "these objects are near a fire".
   *
   * Warm and bright at the hearth, cool and dim at the edge of the dark. The
   * cool end matters as much as the warm one: firelight is orange precisely
   * because everything out of its reach is blue.
   *
   * Each light carries two radii, because they answer different questions.
   *
   * `tintRadius` is how far it *models* — the falloff that makes the clearing
   * look lit. `reachRadius` is how far it carries far enough to make something
   * out, which is a good deal further than the range over which it still looks
   * warm, and is what gates gathering. Collapsing them into one number was the
   * first attempt, and it made every fuel source past the near clearing
   * unreachable at *any* fire size — not hard, impossible, which is the worst
   * kind of boundary: visible, obvious, and permanently dead.
   *
   * A prop takes the *best* it gets from any light. Two half-lit sources do
   * not add up to a lit one — you either have enough light to see by or you
   * don't — and taking the max means carrying the lantern somewhere can only
   * ever open the grove up, never wash out the hearth you left behind.
   *
   * @param lights positions in WORLD space (props live in the world).
   * @param amount 0..1 — how much night is in effect. At noon this is 0 and
   *               every prop returns to its own colour.
   */
  updateLighting(lights: ReadonlyArray<PropLight>, amount: number): void {
    if (amount <= 0.01) {
      if (this.lit) {
        for (const p of this.props) p.img.clearTint();
        this.lit = false;
      }
      // By day everything is reachable — the fire is not the only light.
      for (const p of this.props) p.litness = 1;
      return;
    }
    this.lit = true;

    for (const p of this.props) {
      const img = p.img;
      const best = bestLightAt(lights, img.x, img.y);

      // Full warmth inside the flames, cooling toward the dark.
      const warm = Math.pow(best.tint, 1.4);
      p.litness = best.reach;

      // COLOUR, not brightness. A Phaser tint can only multiply, so pulling the
      // far end dark just crushes props that the night plate has already
      // darkened — the first attempt did exactly that and made the clearing
      // murkier instead of warmer. The far end therefore stays near neutral
      // with a slight blue cast, and all the contrast is carried by hue.
      const r = Math.round(Phaser.Math.Linear(186, 255, warm));
      const g = Math.round(Phaser.Math.Linear(194, 214, warm));
      const b = Math.round(Phaser.Math.Linear(218, 166, warm));

      // Blend in by how dark it is, so dusk is a hint and midnight the full
      // statement, and noon leaves every prop its own colour.
      const mix = (target: number) => Math.round(255 + (target - 255) * amount);
      img.setTint(
        (Phaser.Math.Clamp(mix(r), 0, 255) << 16) |
        (Phaser.Math.Clamp(mix(g), 0, 255) << 8) |
        Phaser.Math.Clamp(mix(b), 0, 255),
      );
    }
  }

  /**
   * Depth for something standing at `ny`, on the same scale as the layers and
   * the walker. Within a bracket, lower on screen means nearer.
   */
  private depthFor(ny: number): number {
    const thresholds = this.spec.depthBracketThresholds;
    const bracket = thresholds?.length ? yToDepthBracket(ny, thresholds) : 0;
    const rank = Math.min(98, Math.max(0, Math.round(ny * 98)));
    return bracket * BRACKET_DEPTH_SPACING + rank;
  }

  destroy(): void {
    for (const p of this.props) p.workTween?.stop();
    for (const { img } of this.props) img.destroy();
    this.props.length = 0;
  }
}
